import { useEventListener } from '@hooks/useEventListener'
import type { WidgetDimension, WidgetMessages } from '@models/types'
import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Session from 'supertokens-auth-react/recipe/session'

type MyBookingWidgetProps = {
  config: {
    apiToken: string
    mode: string
    theme?: string
    integrationType: string
    bookingId?: string
  }
}

const MyBookingWidget: FC<MyBookingWidgetProps> = (props) => {
  const { config } = props
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [dimension, setDimension] = useState<WidgetDimension>({ height: 0, width: 0 })
  const navigate = useNavigate()
  const location = useLocation()

  const setupIframe = useCallback(async () => {
    const iframe = iframeRef.current
    const iframeParams = new URLSearchParams(config).toString()
    const session = await Session.doesSessionExist()

    if (!session) {
      throw new Error('Session does not exist')
    }

    const accessToken = await Session.getAccessToken()

    if (iframe && accessToken) {
      iframe.src = `${import.meta.env.VITE_BOOKING_WIDGET_URL}/v2/booking/?${iframeParams}&jwt=${accessToken}`
    }
  }, [config])

  useEffect(() => {
    setupIframe()
  }, [config, setupIframe])

  const handleEventMessage = (event: MessageEvent<WidgetMessages>) => {
    if (event.data?.type === 'resize') {
      setDimension({
        height: event.data.message.height,
        width: event.data.message.width,
      })
    }

    if (event.data?.type === 'not_found_booking') {
      const searchParams = new URLSearchParams(location.search)
      searchParams.delete('bookingId')
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })
    }
  }

  useEventListener('message', handleEventMessage)

  return (
    <iframe
      data-hj-allow-iframe="true"
      title="Dentech.se my bookings"
      id="tdl-booking-widget"
      ref={iframeRef}
      style={{
        maxWidth: '971px',
        width: '100%',
        height: `${dimension.height}px`,
        border: 'none',
      }}
    />
  )
}

export default MyBookingWidget
